import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import type {
	ActivationChecklistActionType,
	ActivationChecklistCta,
	ActivationChecklistItem,
	ActivationChecklistItemId,
	ActivationChecklistProduct,
	ActivationChecklistRuntimeCapability,
	ActivationChecklistRuntimeContext,
	ActivationChecklistVisibilityRule,
	HydratedActivationChecklistItem,
	VisibilityState,
} from './types';

const exhaustiveActivationChecklistItemIds = <T extends readonly ActivationChecklistItemId[]>(
	ids: T & ([ActivationChecklistItemId] extends [T[number]] ? unknown : never),
) => ids;

const activationChecklistItemIds = exhaustiveActivationChecklistItemIds([
	'jira-create-work-item',
	'jira-update-work-item',
	'jira-add-comment',
	'jira-invite-team',
] as const);

const activationChecklistItemIdSet = new Set<string>(activationChecklistItemIds);

const isActivationChecklistItemId = (id: unknown): id is ActivationChecklistItemId =>
	typeof id === 'string' && activationChecklistItemIdSet.has(id);

const getOrigin = (url: string | undefined): string | undefined => {
	if (!url) {
		return undefined;
	}
	try {
		return new URL(url).origin;
	} catch {
		return undefined;
	}
};

const getIssueProjectKey = (issueUrl: string | undefined): string | undefined => {
	if (!issueUrl) {
		return undefined;
	}
	const match = issueUrl.match(/\/browse\/([A-Z][A-Z0-9]*)-\d+/i);
	return match?.[1]?.toUpperCase();
};

const getIssueUrl = (issueUrl: string | undefined): string | undefined =>
	getIssueProjectKey(issueUrl) ? issueUrl : undefined;

const withSearchParam = (
	href: string | undefined,
	key: string,
	value: string,
	hash?: string,
): string | undefined => {
	if (!href) {
		return undefined;
	}
	try {
		const url = new URL(href);
		url.searchParams.set(key, value);
		if (hash) {
			url.hash = hash;
		}
		return url.href;
	} catch {
		return undefined;
	}
};

const getCreateWorkItemHref = (siteOrigin: string): string => {
	const url = new URL('/jira/for-you', siteOrigin);

	url.searchParams.set('createissue', 'true');
	url.searchParams.set('gicTriggerSource', 'ucAtlassianHomeCreateIssue');
	url.searchParams.set('tab', 'workedon');

	return url.href;
};

const hasRuntimeCapability = (
	capability: ActivationChecklistRuntimeCapability,
	targets: ActivationChecklistRuntimeContext,
): boolean => {
	switch (capability) {
		case 'inviteTeam':
			return targets.invite?.canInvite === true;
		default: {
			const exhaustiveCapability: never = capability;
			void exhaustiveCapability;
			return false;
		}
	}
};

const matchesVisibilityRule = (
	rule: ActivationChecklistVisibilityRule,
	targets: ActivationChecklistRuntimeContext,
): boolean => {
	switch (rule.type) {
		case 'capability':
			return hasRuntimeCapability(rule.capability, targets);
		default: {
			const exhaustiveRuleType: never = rule.type;
			void exhaustiveRuleType;
			return false;
		}
	}
};

export const shouldShowActivationChecklistMessage = (
	message: Pick<ActivationChecklistItem, 'product' | 'visibility'> & Partial<VisibilityState>,
	product: ActivationChecklistProduct,
	targets: ActivationChecklistRuntimeContext = {},
): boolean =>
	message.visible !== false &&
	message.product === product &&
	message.visibility.every((rule) => matchesVisibilityRule(rule, targets));

export const resolveActivationChecklistHref = (
	cta: ActivationChecklistCta,
	targets: ActivationChecklistRuntimeContext = {},
): string | undefined => {
	const fallbackHref = cta.action.fallbackHref;

	switch (cta.action.type) {
		case 'createWorkItem': {
			const siteOrigin = getOrigin(targets.site?.url);
			if (!siteOrigin) {
				return fallbackHref;
			}
			return getCreateWorkItemHref(siteOrigin);
		}
		case 'updateWorkItem': {
			return (
				withSearchParam(
					getIssueUrl(targets.workItem?.url),
					'activationChecklistAction',
					'edit-field',
				) ?? fallbackHref
			);
		}
		case 'addComment': {
			return (
				withSearchParam(
					getIssueUrl(targets.workItem?.url),
					'activationChecklistAction',
					'add-comment',
					'add-comment',
				) ?? fallbackHref
			);
		}
		case 'inviteTeam':
			return targets.invite?.href ?? fallbackHref;
	}
};

export type ResolvedCta = {
	label: string;
	href: string;
};

export type ChecklistRenderMessage = EligibleMessage &
	Pick<ActivationChecklistItem, 'id' | 'title' | 'description' | 'order'> & {
		actionType: ActivationChecklistActionType;
		completed: boolean;
		cta?: ResolvedCta;
	};

export type ServerResolvedMessage = EligibleMessage & HydratedActivationChecklistItem;

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const hasValidActivationChecklistContext = (
	context: unknown,
): context is HydratedActivationChecklistItem => {
	if (!isRecord(context)) {
		return false;
	}

	const cta = context.cta;
	const action = isRecord(cta) ? cta.action : undefined;

	return (
		isActivationChecklistItemId(context.id) &&
		typeof context.product === 'string' &&
		typeof context.order === 'number' &&
		typeof context.title === 'string' &&
		typeof context.description === 'string' &&
		typeof context.visible === 'boolean' &&
		typeof context.completed === 'boolean' &&
		Array.isArray(context.visibility) &&
		isRecord(cta) &&
		typeof cta.label === 'string' &&
		isRecord(action) &&
		typeof action.type === 'string' &&
		typeof action.fallbackHref === 'string'
	);
};

export const resolvePlacementMessages = (
	messages: readonly ServerResolvedMessage[],
): ServerResolvedMessage[] =>
	messages.flatMap((message) => {
		const nestedContext = (message as { context?: unknown }).context;
		const context = hasValidActivationChecklistContext(nestedContext) ? nestedContext : message;

		if (!hasValidActivationChecklistContext(context)) {
			return [];
		}

		return [
			{
				...context,
				...message,
			},
		] as ServerResolvedMessage[];
	});

export const resolveDismissibleMessages = (
	messages: readonly ServerResolvedMessage[],
	product: ActivationChecklistProduct,
	dismissedMessageIds: ReadonlySet<string>,
): ServerResolvedMessage[] =>
	messages.filter(
		(message) => message.product === product && !dismissedMessageIds.has(message.messageInstanceId),
	);

export type ActivationChecklistItemClickHandler = (item: {
	id: ActivationChecklistItemId;
	actionType: ActivationChecklistActionType;
}) => void;

export type ActivationChecklistRenderedItem = {
	id: ActivationChecklistItemId;
	completed: boolean;
};

export type ActivationChecklistItemsRenderedHandler = (
	items: readonly ActivationChecklistRenderedItem[],
) => void;

const isItemCompleted = (
	item: { id: string; completed?: boolean },
	completedItemIds: ReadonlySet<string>,
): boolean => item.completed === true || completedItemIds.has(item.id);

const resolveMessageCta = (
	item: ActivationChecklistItem,
	targets: ActivationChecklistRuntimeContext,
	completed: boolean,
): ResolvedCta | undefined => {
	if (completed) {
		return undefined;
	}

	const href = resolveActivationChecklistHref(item.cta, targets);

	if (!href) {
		return undefined;
	}

	return {
		label: item.cta.label,
		href,
	};
};

const sortMessagesByCompletionThenOrder = <T extends { completed: boolean; order: number }>(
	items: readonly T[],
): T[] =>
	[...items].sort((a, b) => {
		if (a.completed !== b.completed) {
			return a.completed ? 1 : -1;
		}

		return a.order - b.order;
	});

export const resolveVisibleMessages = (
	messages: readonly ServerResolvedMessage[],
	product: ActivationChecklistProduct,
	targets: ActivationChecklistRuntimeContext,
): ChecklistRenderMessage[] => {
	const completedItemIds = new Set(targets.completedItemIds ?? []);

	return sortMessagesByCompletionThenOrder(
		messages
			.filter((message) => shouldShowActivationChecklistMessage(message, product, targets))
			.map((message) => {
				const completed = isItemCompleted(message, completedItemIds);
				return {
					...message,
					actionType: message.cta.action.type,
					completed,
					cta: resolveMessageCta(message, targets, completed),
				};
			}),
	);
};

export const sortMessagesByOrder = <T extends { order: number }>(items: readonly T[]): T[] =>
	[...items].sort((a, b) => a.order - b.order);
